import { Experience } from "@/types/supabase";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";
import { optimizedSupabaseImage } from "@/lib/utils";

interface ExperienceCardProps {
  experience: Experience;
  index: number;
  showResponsibilities: boolean | number;
}

const ExperienceCard = ({
  experience,
  index,
  showResponsibilities,
}: ExperienceCardProps) => {
  const isMobile = useIsMobile();
  const [showAll, setShowAll] = useState(false);
  const companyInitial = (experience.company || experience.title || "?")
    .charAt(0)
    .toUpperCase();

  let responsibilitiesToShow = experience.responsibilities;
  let showExpandButton = false;
  if (showResponsibilities && typeof showResponsibilities === "number") {
    showExpandButton = experience.responsibilities.length > showResponsibilities;
    if (!showAll) {
      responsibilitiesToShow = experience.responsibilities.slice(0, showResponsibilities);
    }
  }

  return (
    <div className="relative mb-8 md:mb-12">
      <div
        className={`absolute ${
          isMobile ? "left-3" : "left-0"
        } w-3 md:w-4 h-3 md:h-4 rounded-full bg-teal transform -translate-x-1/2 z-10 `}
      ></div>

      <div
        className={`${isMobile ? "ml-6" : "ml-8"} animate-fade-in`}
        style={{ animationDelay: `${0.2 + index * 0.1}s` }}
      >
        <div className="glass-morphism rounded-xl p-4 md:p-6">
          <div className="flex items-start justify-between gap-4 md:gap-5">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg md:text-xl font-bold text-green-400 uppercase break-words mb-1">
                {experience.title}
              </h3>

              <p className="text-gray-400 text-sm md:text-base italic mb-3">
                {experience.company}
              </p>

              <div className="bg-gray-800 inline-block px-2 md:px-3 py-1 rounded-sm">
                <p className="text-xs md:text-sm text-gray-300">
                  {experience.period}
                </p>
              </div>
            </div>

            <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-gray-900 border border-gray-800 flex items-center justify-center overflow-hidden flex-shrink-0">
              {experience.logo_url ? (
                <img
                  src={optimizedSupabaseImage(experience.logo_url, 112)}
                  alt={`${experience.company} logo`}
                  className="w-full h-full object-contain"
                  loading="lazy"
                  decoding="async"
                  width="112"
                  height="112"
                />
              ) : (
                <span className="text-sm md:text-base font-semibold text-gray-300">
                  {companyInitial}
                </span>
              )}
            </div>
          </div>
          {showResponsibilities && (
            <>
            <ul className="space-y-2 mb-4 mt-4">
              {responsibilitiesToShow.map((responsibility, idx) => (
                <li key={idx} className="flex items-start">
                  <div className="mt-1.5 mr-2 md:mr-3 h-1.5 w-1.5 rounded-full bg-teal flex-shrink-0"></div>
                  <span className="text-gray-300 text-xs md:text-sm">
                    {responsibility}
                  </span>
                </li>
              ))}
            </ul>
              {showExpandButton && (
                <button
                  className="text-teal flex items-center gap-1 text-xs md:text-sm mb-2 focus:outline-none"
                  onClick={() => setShowAll((prev) => !prev)}
                  type="button"
                >
                  {showAll ? (
                    <>
                      <span>Hide</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 15l-7-7-7 7" /></svg>
                    </>
                  ) : (
                    <>
                      <span>Expand</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </>
                  )}
                </button>
              )}
            </>
          )}

          {/* Skills/Technologies */}
          {experience.tags && experience.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 md:gap-2 mt-4">
              {experience.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs bg-gray-800 text-gray-200 px-2 md:px-3 py-0.5 md:py-1 rounded"
                >
                  {tag.toUpperCase()}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExperienceCard;
